
import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject, Fragment}  from 'react';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { SnackbarAction, SnackbarKey, useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation  } from 'react-i18next';

import colorNameList from 'color-name-list';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';


import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import ShieldIcon from '@mui/icons-material/Shield';

import PlaylistAddCircleIcon from '@mui/icons-material/PlaylistAddCircle';
import QueueIcon from '@mui/icons-material/Queue';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

import BackpackIcon from '@mui/icons-material/Backpack';

import BoltIcon from '@mui/icons-material/Bolt';

import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { MdOutlineAdd } from 'react-icons/md';

import { ChromePicker, ColorResult } from 'react-color';



import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom, isSaveLoadingAtom, currentUserSessionAtom } from 'library/store';
import useProductService, { useBasicFilterProduct, useBasicFilterProductSearch } from './services/Product';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Tabs, Tab, Typography, Chip, Popover, useTheme } from '@mui/material';
import NumberFormat from 'react-number-format';

import { IExamInsurance, IFormationBillingType, IPackagingProduct, IProduct, IProductOption, IRentalResource, IServiceTask, IShipingDensityPrice, ProductType, defaultProduct, defaultProductPrice } from './models/Product';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';

import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';

import useApplicationSetupService from 'features/setup/services/ApplicationSetup';

import useDeliveryService from 'features/configuration/services/Delivery';

import useUtils from 'library/utils';

import IEnumeration, { Enum_LINE_OF_BUSINESS, Enum_SERVICE_TASK, Enum_FORMATION_BILLING_TYPE, Enum_ARTICLE_OPTION_CLASS, 
      Enum_MAINTENANCE_CATEGORY_CLASS, Enum_CONTRACT_SCOPE_CLASS, Enum_PERSON_SERVICE ,IEnumerationItem, Enum_ARTICLE_WRAPPING, 
      Enum_RENTAL_PERIODICITY, Enum_CURRENCY,
      Enum_CONSUMPTION_OPTION_CLASS, Enum_EXAM_TYPE,
      Enum_INSURANCE} from 'features/configuration/models/Enumeration';

import {ISharing} from 'features/setup/models/Sharing';
import useSharingService, { useBasicFilterSharing } from 'features/setup/services/Sharing';

import { defaultExtension, IExtension, IExtensionType  } from 'features/configuration/models/ExtensionType';
import useExtensionTypeService, { useBasicFilterExtensionType } from 'features/configuration/services/ExtensionType';

import { justifyCenter, typographyGroupBoxStyling, carouselImage } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';
import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { debounce, sum } from 'lodash';
import { GrClose, GrSearch } from 'react-icons/gr';
import EnhancedTable from 'components/ui/EnhancedTable';
import { IArticleStock } from 'features/configuration/models/Delivery';
import { useBasicFilterResource } from 'features/production/services/Resource';
import { IResource } from 'features/production/models/Resource';
import BasicImportForm from 'components/ui/BasicImportForm';
import { ExtensionFormDialog } from 'components/ExtensionFormDialog';
import { IFamilyProduct } from './models/FamilyProduct';
import { useBasicFilterFamilyProduct } from './services/FamilyProduct';
import { DialogEnumerationItemForm } from 'features/configuration/DialogEnumerationItemForm';
import BasicButtonList from 'components/ui/BasicButtonList';


type ExtensionUsage = 'product-A' | 'product-U';


export const ProductForm: FC<IProduct> = (props: IProduct = defaultProduct) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();  
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const theme = useTheme();

  const {id} = useParams();
  
  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const { applicationSetup, language: lg, roleEntities} = useRecoilValue(currentUserSessionAtom);

  const {retrieveEntity, retrieveData, openEntityActionDrawer, 
      checkEntityExpressionSyntax, checkEntitySaveAuthorization } = entityService();

  const {getEnumerationItemsByEnumerationCodes, getAsOptions } = useEnumerationService();

  const {getDefaultProductType} = useApplicationSetupService();

  const {getArticlePerStores } = useDeliveryService();

  const { createProduct, createBasicImportProduct ,updateProduct, getProductsSearch, getProductsSearchCount } = useProductService();

  const {capitalizeFirstLetter} = useUtils();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const colors = colorNameList.filter(c => c.name.split(' ').length === 1 && c.name.length <= 5).map((color) => color);
  
  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterProduct = useBasicFilterProduct( 0,
    (event: React.MouseEvent<unknown>, row: IProduct) => {
        setIsSearchBoxShow(false);
        _setId(row.id);
      }
  );

  const emptyFunc = (obj: any) => {}

  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity( 
      (event: React.MouseEvent<unknown>, row: IEntity) => {
          const {name, description} = row;

          // setValue('entityName', name || '');
          // setValue('entityDescription', description || '');
                           
          setOpenEntityFilter(false);
      }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration( 
      (event: React.MouseEvent<unknown>, row: IEnumeration) => {
          const {id, name, description} = row;

          // setValue('enumerationId', id);
          // setValue('enumerationName', name);
                           
          setOpenEnumerationFilter(false);
      }
  );

  const [openFamilyProductFilter, setOpenFamilyProductFilter] = useState(false);
  const basicFilterFamilyProduct = useBasicFilterFamilyProduct( 
      (event: React.MouseEvent<unknown>, row: IFamilyProduct) => {
          const {id, name, description} = row;

          setValue('familyProductId', id);
          setValue('familyProductName', name);
                           
          setOpenFamilyProductFilter(false);
      }
  );


  const [openSharingFilter, setOpenSharingFilter] = useState(false);
  const basicFilterSharing = useBasicFilterSharing( 
      (event: React.MouseEvent<unknown>, row: ISharing) => {
          const {id, name, description} = row;

          setValue('sharingId', id);
          setValue('sharingName', name);
                           
          setOpenSharingFilter(false);
      }
  );
  
  type ProductFilterUsageType = 'packaging' | 'stock';

  const [currentProductOptions, setCurrentProductOptions] = useState<IProductOption[]>([]);
  
  const [showProductOption, setShowProductOption] = useState<boolean>(false);
  const [currentArticle2Add, setCurrentArticle2Add] = useState<IProduct>({...defaultProduct});

  const [productFilterUsage, setProductFilterUsage] = useState<ProductFilterUsageType>('packaging');
  const [openProductUsageFilter, setOpenProductUsageFilter] = useState(false);
  const basicFilterProductUsage = useBasicFilterProduct( 0,
      (event: React.MouseEvent<unknown>, row: IProduct) => {
          const {id, name, description, type, isDirectStock ,productOptions} = row;

          //if( ['packaging', 'formation', 'maintenance', 'contract'].includes(type) ) return;

          if(productFilterUsage === 'packaging') {
            if( type !== 'service' && type !== 'article' ) return;
          
            if(type === 'service')
              (refAppendPackagingProducts.current??emptyFunc)({id: 0, type, packagingId: _id, productId: id, 
                quantity: 0, productOptionId: 0, productOptionName: '', part: 0,
                serviceBeneficiaryPointExpression: '',  productName: name , personServiceCode: '' });
            else if(type === 'article'){
              setCurrentArticle2Add(row);
              setCurrentProductOptions(productOptions);

              setShowProductOption(true);   
            }
          } else if(productFilterUsage === 'stock') {
            if( type !== 'article' || !isDirectStock ) return;
            
            setCurrentArticle2Add(row);
            setCurrentProductOptions(productOptions);

            setShowProductOption(true);  
          }
          
                           
          setOpenProductUsageFilter(false);
      }
  );

  const [openResourceFilter, setOpenResourceFilter] = useState(false);
  const basicFilterResource = useBasicFilterResource( 
      (event: React.MouseEvent<unknown>, row: IResource) => {
          const {id, description,permanentResourceId: resourceTypeId, capacity ,permanentResourceName: resourceTypeName} = row;
        
          const findResource = getValues().rentalResources.find(x => x.resourceId === id);
          if(!isFalsy(findResource)) return;
          
          (refAppendRentalResources.current??emptyFunc)({id: 0, rentalId: _id, resourceId: id, isCapacityFixed: true,
            defaultCapacity: capacity, resourceName: description });                          
        setOpenResourceFilter(false);
      }
  );

  const {canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
      enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
      onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();

  const methods = useForm<IProduct>({defaultValues: {...defaultProduct, 
    currentCurrencyCode: applicationSetup.defaultCurrencyCode,
    type: getDefaultProductType() }});

  const { register, setValue ,getValues, watch, reset ,handleSubmit ,control , formState: { errors } } = methods;

  let { fields, append: appendExtensions, update: updateExtension ,remove: removeProductExtension,  } = useFieldArray({
    name: `extensions`,
    control,            
  });

  let { fields: productPrices, append: appendProductPrices, update: updateProductPrice ,  } = useFieldArray({ name: 'productPrices', control, });

  // const watchIsTaxable = watch('isTaxable');
  // const watchIsDefaultTaxFormula = watch('isDefaultTaxFormula');

  const watchBase64Picture = watch('base64Picture');

  const watchSharingId = watch('sharingId');    
  const watchProductType = watch('type');    
  const watchCode = watch('code');    

  const watchName = watch('name');    
  const watchDescription = watch('description');    

  const watchCurrentCurrencyCode = watch('currentCurrencyCode');

  const watchCurrentPriceExpression = watch('currentPriceExpression');

  const watchInsuranceCoverageOption = watch('insuranceCoverageOption');

  const watchIsDirectStock = watch('isDirectStock');
  const watchStockProductId = watch('stockProductId');

  const [openProductSearchFilter, setOpenProductSearchFilter] = useState(false);
  const basicFilterProductSearch = useBasicFilterProductSearch( 
      () => {
        const { name, description} = getValues();

        return {name, description};
      },      
      (event: React.MouseEvent<unknown>, row: IProduct) => {
        const {id, name, description} = row;
        
        _setId(row.id);
        setValue('id', id);
        setValue('name', name);
        setValue('description', description); 

        setOpenProductSearchFilter(false);
      }
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [color, setColor] = useState<string>('#000');

  // const watchParentProductId = watch('parentProductId');

  // let { fields, append: appendCoverages, update: updateCoverage ,remove: removeCoverage,  } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
  //   name: `productCoverages`,
  //   control,            
  // });
  

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<IProduct>,Error,IProduct>(
      _id>0?updateProduct:createProduct, {   
        onSuccess: async (data: IResult<IProduct>) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                   
          setIsSaveLoading(false);
          _setId(data.data.id);
          //setCurrentEntityIdForAction(data.data.id);

          queryClient.invalidateQueries(['Product',_id]);        
          await retrieveData('Product',_id, refetch);        
          reset({..._data, currentCurrencyCode: applicationSetup.defaultCurrencyCode } as unknown as IProduct);

          setCurrentFormNameAtom(`${t('Product')} - # ${data.data.id} # ` );
          //queryClient.invalidateQueries(['Product',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });

      const {isLoading: isLoadingBasicImport, isError: isErrorBasicImport, error: errorBasicImport,mutate: mutateBasicImport } = useMutation<IResult<boolean>,Error,IProduct[]>(
        createBasicImportProduct, {   
          onSuccess: async (data: IResult<boolean>) => {
            enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                  anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                     
            setIsSaveLoading(false);
            
            //_setId(data.data.id);
            //setCurrentEntityIdForAction(data.data.id);
            
            //queryClient.invalidateQueries(['Product',data.data.id]);
          },
          onError: (err: Error) => {          
            enqueueSnackbar( error?.message, { variant: 'error',
                  anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
            setIsSaveLoading(false);
          }
        });

    const {data: _data, refetch} = useQuery<IProduct>(['Product', _id], () => retrieveEntity('Product',_id), 
      {refetchOnWindowFocus: false ,enabled: false } );

      const {data: enumItems, refetch: refetchEnumerationItems} = useQuery<IEnumerationItem[]>(
        ['EnumerationItems', 'Product'], () => 
          getEnumerationItemsByEnumerationCodes( [Enum_LINE_OF_BUSINESS, Enum_CURRENCY,
              Enum_SERVICE_TASK, Enum_FORMATION_BILLING_TYPE, Enum_ARTICLE_OPTION_CLASS, Enum_ARTICLE_WRAPPING,
              Enum_MAINTENANCE_CATEGORY_CLASS, Enum_CONTRACT_SCOPE_CLASS, Enum_PERSON_SERVICE, 
              Enum_RENTAL_PERIODICITY, Enum_CONSUMPTION_OPTION_CLASS, Enum_EXAM_TYPE, Enum_INSURANCE ] ));

      const [valueTabCoverage, setValueTabCoverage] = useState<number>(0);

      const handleTabCoverageChange = (event: React.SyntheticEvent, newValue: number) => {  
        
        setValueTabCoverage(newValue);       
      };

    
    const handleProductType = ( event: React.MouseEvent<HTMLElement>, newProductType: ProductType ) => {

      if(newProductType === null) return;
      setValue('type', newProductType);
    };

    const handleCurrentCurrency = ( event: React.MouseEvent<HTMLElement>, newCurencyCode: string ) => {

      if(newCurencyCode === null) return;
      setValue('currentCurrencyCode', newCurencyCode);
    };

    const { control: controlExtension, setValue: setValueExtension, getValues: getValuesExtension, reset: resetExtension } = useForm<IExtension>({defaultValues:defaultExtension});
  

  const [extensionUsage, setExtensionUsage] = useState<ExtensionUsage>('product-A');
  
  const [extensionIndex, setExtensionIndex] = useState<number>(-1);
  const [openExtension, setOpenExtension] = useState(false);
  const handleClickAddExtension = (event: any, extUsage: ExtensionUsage) => {
    
    setExtensionUsage(extUsage);

    setValueExtension('alias', '');
    setValueExtension('description', '');
    setValueExtension('extensionTypeId', 0);
    setValueExtension('extensionTypeName', '');
    setValueExtension('extensionTypeType', 'text');
    setValueExtension('extensionTypeBaseType', 'string');
    
    setOpenExtension(true);
  }

  const handleClickEditExtension = (index: number, extUsage: ExtensionUsage) => {

    if(extUsage === 'product-U') {
      const productExtension = getValues().extensions.at(index);
      if(isFalsy(productExtension)) return;     

      resetExtension({...productExtension!});      
    } else return;
    
    setExtensionUsage(extUsage);
    setExtensionIndex(index);    
    
    setOpenExtension(true);
  }

  const handleOkExtension =  (event : MouseEvent<HTMLButtonElement>) => {
    const { alias, description, extensionTypeId, extensionTypeName, extensionTypeType, extensionTypeBaseType } = getValuesExtension();

    if(isFalsy(alias) || isFalsy(description) || isFalsy(extensionTypeId)) return;

    if(extensionUsage === 'product-A') {
      if(getValues().extensions.some(ext => ext.alias === alias)) return;
        
      appendExtensions( { id: 0, type: 'product', alias, description, extensionTypeId, extensionTypeName, extensionTypeType, extensionTypeBaseType});
    }      
    else if(extensionUsage === 'product-U') {
      const extension = getValues().extensions.at(extensionIndex);
      if(isFalsy(extension)) return;

      if( getValues().extensions.findIndex( ext => ext.alias === alias) !== extensionIndex) return;

      updateExtension(extensionIndex, {...extension, alias, description, extensionTypeId, 
            extensionTypeName, extensionTypeType, extensionTypeBaseType});
    } 

    setOpenExtension(false);    
  }

      const [openEntityExpression, setOpenEntityExpression] = useState(false);
      const [currentExpression, setCurrentExpression] = useState(''); 
      const [currentEntityName, setCurrentEntityName] = useState(''); 
      const [currentReturnType, setCurrentReturnType] = useState(''); 

      type ExpressionType = 'price' | 'tax' | 'max-discount' | 'max-loaded' | 'price-unity' | 'customer-point' | 'service-point' | 'packaging-service-point';
      const [currentExpressionType, setCurrentExpressionType] = useState<ExpressionType>('price');

      const handleClickOpenExpression = (event: any, expressionType: ExpressionType, 
          entityName: string, returnType: string, expression: string) => {

        setCurrentExpressionType(expressionType);
        setCurrentEntityName(entityName);
        setCurrentReturnType(returnType);
        setCurrentExpression(expression);
        setOpenEntityExpression(true);
      }

      const handleClickOkExpression = async () => {

        const checkExpression = await checkEntityExpressionSyntax(currentEntityName, currentExpression);
        if(!checkExpression){
          enqueueSnackbar( t('Expression evaluation error'), { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
          return;
        }

        const {syntaxOk, syntaxError, returnType} = checkExpression;
        if(!syntaxOk) {
          enqueueSnackbar( syntaxError , { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
          return;
        }
       
        if(returnType !== currentReturnType) {
          enqueueSnackbar( `${t('The result of expression must be')} ${currentReturnType}` , { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
                return;
        }
      
        
        if( ['price', 'tax', 'max-discount', 'max-loaded' ,'price-unity', 'customer-point', 'service-point'].includes(currentExpressionType)  ) {

          const idx = getValues().productPrices.findIndex(p => p.currencyCode === watchCurrentCurrencyCode);

          const priceExpression = currentExpressionType === 'price' ? currentExpression: getValues().productPrices[idx].priceExpression;
          const taxExpression = currentExpressionType === 'tax' ? currentExpression: getValues().productPrices[idx].taxExpression;
          const maxDiscountExpression = currentExpressionType === 'max-discount' ? currentExpression: getValues().productPrices[idx].maxDiscountExpression;
          const maxLoadedExpression = currentExpressionType === 'max-loaded' ? currentExpression: getValues().productPrices[idx].maxLoadedExpression;
          //const priceExpressionUnity = currentExpressionType === 'price-unity' ? currentExpression: getValues().priceExpressionUnity;

          const customerPointExpression = currentExpressionType === 'customer-point' ? currentExpression: getValues().customerPointExpression;
          const serviceBeneficiaryPointExpression = currentExpressionType === 'service-point' ? currentExpression: getValues().serviceBeneficiaryPointExpression;

          setValue(`productPrices.${idx}.priceExpression`, priceExpression, {shouldValidate: true});
          setValue(`productPrices.${idx}.taxExpression`, taxExpression);
          setValue(`productPrices.${idx}.maxDiscountExpression`, maxDiscountExpression);
          setValue(`productPrices.${idx}.maxLoadedExpression`, maxLoadedExpression);
          //setValue('priceExpressionUnity', priceExpressionUnity);

          setValue('customerPointExpression', customerPointExpression);
          setValue('serviceBeneficiaryPointExpression', serviceBeneficiaryPointExpression);
        } else if( ['packaging-service-point'].includes(currentExpressionType) ) {

          const packagingService = getValues().packagingProducts.at(packagingProductIndex);
          if(isFalsy(packagingService)) return;

          (refUpdatePackagingProduct.current??emptyFunc)(packagingProductIndex,{...packagingService, 
              serviceBeneficiaryPointExpression: currentExpression });
        }

        setOpenEntityExpression(false);
      }

      const handleClickOpenSharing = (event: any) => {        
        setOpenSharingFilter(true);
      }

      const handleClickRemoveSharing = (event: any) => {
        setValue('sharingId', 0);
        setValue('sharingName', '');     
      }

      const handleClickOpenFamilyProduct = (event: any) => {   
        
        setOpenFamilyProductFilter(true);
      }

      const handleClickRemoveFamilyProduct = (event: any) => {
        setValue('familyProductId', 0);
        setValue('familyProductName', '');     
      }

      const handleClickOpenStockProduct = (event: any) => {
        setProductFilterUsage('stock');
        setOpenProductUsageFilter(true);
      }

      const handleClickRemoveStockProduct = (event: any) => {
        setValue('stockProductId', 0);
        setValue('stockProductName', '');
        setValue('stockProductOptionId',0);
        setValue('stockProductOptionName', '');
      }
      
          
      const cellEditableProductOption = (row: IProductOption, cellId: keyof IProductOption) => {
        return true;
      }
      
      const [headProductOptionCells, setHeadProductOptionCells]  = useState<HeadCell<IProductOption>[]>([      
        //{id:'userId', label : t('Id'),  display: true, type: 'string', width: 5 },
        {id:'code', label : t('Code'),  display: true, type: 'string', width: 20, isEditable: cellEditableProductOption},
        {id:'name', label : `${t('Name')}`,  display: true, type: 'string', width: 30, isEditable: cellEditableProductOption},       
        {id:'isActive', label : `${t('Active ?')}`,  display: true, type: 'boolean', width: 10, isEditable: cellEditableProductOption},       
        {id:'description', label : `${t('Description')}`,  display: true, type: 'string', width: 30, isEditable: cellEditableProductOption},       
        {id:'hasPicture', label : `${t('Picture')} ?`,  display: true, type: 'boolean', width: 10},       
      ]);
  
      const refAppendProductOptions = useRef<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>(null);
      const refUpdateProductOption = useRef<(index: number,value: Partial<FieldArray<IProduct>> ) => void>(null);
      const refRemoveProductOption = useRef<(index: number ) => void>(null);
        
      const handleAddProductOptions = (event: any) => {
        (refAppendProductOptions.current??emptyFunc)({id: 0,  productId: 0,
          code: '', name: '', description: '', isActive: true, fileName: '', base64Picture: '', hasPicture: false});
      }
  
      const productOptionRowActionIcon = ( productOption: IProductOption) : ActionIconTableRow<IProduct,IProductOption> => {
    
        const res: ActionIconTableRow<IProduct,IProductOption> = {
          toolTip: 'remove',
          icon: RemoveCircleIcon,
          hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
          isActionExecuting: true,
          onRowClickIcon: (event : any,index: number, row: IProductOption) => {
            
             (refRemoveProductOption.current??emptyFunc)(index);            
          }
        }
        return res;
    }
    

    const getServiceTaskCode = (row: IServiceTask, cellId: keyof IServiceTask, 
        opts: {value: string|number, name: string}[]) : {value: string|number, name: string}[]=> {        
        
          return getAsOptions(refEnumItems.current ?? [],Enum_SERVICE_TASK);
      }
  
      const cellEditableServiceTask = (row: IServiceTask, cellId: keyof IServiceTask) => {
        return true;
      }
  
      const [headServiceTaskCells, setHeadServiceTaskCells]  = useState<HeadCell<IServiceTask>[]>([      
        //{id:'userId', label : t('Id'),  display: true, type: 'string', width: 5 },
        {id:'taskCode', label : t('Task'),  display: true, type: 'string', width: 70, isEditable: cellEditableServiceTask,
          getOptions: getServiceTaskCode   },
        {id:'part', label : `${t('Part')}(%)`,  display: true, type: 'numeric', width: 30, 
            isEditable: cellEditableServiceTask, decimalScale: 4 },       
      ]);
  
      const refAppendServiceTasks = useRef<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>(null);
      const refUpdateServiceTask = useRef<(index: number,value: Partial<FieldArray<IProduct>> ) => void>(null);
      const refRemoveServiceTask = useRef<(index: number ) => void>(null);
        
      const handleAddServiceTasks = (event: any) => {
        (refAppendServiceTasks.current??emptyFunc)({id: 0, serviceId: _id, taskCode: '', part: 0});
      }
  
      const serviceTaskRowActionIcon = ( tarificationMatrix: IServiceTask) : ActionIconTableRow<IProduct,IServiceTask> => {
    
        const res: ActionIconTableRow<IProduct,IServiceTask> = {
          toolTip: 'remove',
          icon: RemoveCircleIcon,
          hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
          isActionExecuting: true,
          onRowClickIcon: (event : any,index: number, row: IServiceTask) => {
            
             (refRemoveServiceTask.current??emptyFunc)(index);            
          }
        }
        return res;
    }



    const getInsuranceCode = (row: IExamInsurance, cellId: keyof IExamInsurance, 
      opts: {value: string|number, name: string}[]) : {value: string|number, name: string}[]=> {        
      
        return getAsOptions(refEnumItems.current ?? [],Enum_INSURANCE);
    }

    const cellEditableInsurance = (row: IExamInsurance, cellId: keyof IExamInsurance) => {
      return true;
    }

    const [headExamInsuranceCells, setHeadExamInsuranceCells]  = useState<HeadCell<IExamInsurance>[]>([      
      //{id:'userId', label : t('Id'),  display: true, type: 'string', width: 5 },
      {id:'insuranceCode', label : t('Insurance'),  display: true, type: 'string', width: 100, isEditable: cellEditableInsurance,
        getOptions: getInsuranceCode   },
      // {id:'part', label : `${t('Part')}(%)`,  display: true, type: 'numeric', width: 30, 
      //     isEditable: cellEditableServiceTask, decimalScale: 4 },       
    ]);

    const refAppendExamInsurances = useRef<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>(null);
    const refUpdateExamInsurance = useRef<(index: number,value: Partial<FieldArray<IProduct>> ) => void>(null);
    const refRemoveExamInsurance = useRef<(index: number ) => void>(null);
      
    const handleAddExamInsurances = (event: any) => {
      (refAppendExamInsurances.current??emptyFunc)({id: 0, examId: _id, insuranceCode: ''});
    }

    const handleAddInsurances = (event: any) => {
      onAddItemClick(Enum_INSURANCE, t('Insurance'), t('Insurance'));
    }
    

    const examInsuranceRowActionIcon = ( examInsurance: IExamInsurance) : ActionIconTableRow<IProduct,IExamInsurance> => {
  
      const res: ActionIconTableRow<IProduct,IExamInsurance> = {
        toolTip: 'remove',
        icon: RemoveCircleIcon,
        hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
        isActionExecuting: true,
        onRowClickIcon: (event : any,index: number, row: IExamInsurance) => {
          
           (refRemoveExamInsurance.current??emptyFunc)(index);            
        }
      }
      return res;
  }


    const [packagingProductIndex, setPackagingProductIndex] = useState<number>(-1);
    const getInputAdornmentServicePointExpression = (row: IPackagingProduct, cellId: keyof IPackagingProduct)  => ({
 
      toolTip: 'Details',
      icon: DeveloperModeIcon,
      iconDisable: (row.type === 'article'),
      onClickIcon: (event: any, index: number, row: IPackagingProduct ) => {    
        
        setPackagingProductIndex(index);
        handleClickOpenExpression(event, 'packaging-service-point', 'BillingPackagingService', 'numeric',row.serviceBeneficiaryPointExpression)
      }  
    })

    const getPersonService = (row: IPackagingProduct, cellId: keyof IPackagingProduct, 
      opts: {value: string|number, name: string}[]) : {value: string|number, name: string}[]=> {        
      
        return getAsOptions(refEnumItems.current ?? [],Enum_PERSON_SERVICE);
    }

    const cellEditablePackagingProductQuantity = (row: IPackagingProduct, cellId: keyof IPackagingProduct) => {
      if(row.type === 'article' && cellId === 'quantity') return true;
      return false;
    }

    const cellEditablePackagingProductService = (row: IPackagingProduct, cellId: keyof IPackagingProduct) => {
      if(row.type === 'service' && cellId === 'personServiceCode') return true;
      return false;
    }

    const cellEditablePackagingProduct = (row: IPackagingProduct, cellId: keyof IPackagingProduct) => {
        return true;
    }

    const [headPackagingProductCells, setHeadPackagingProductCells]  = useState<HeadCell<IPackagingProduct>[]>([]);
    useEffect(() => {
      setHeadPackagingProductCells([      
        //{id:'userId', label : t('Id'),  display: true, type: 'string', width: 5 },
        {id:'productName', label : t('Product'),  display: true, type: 'string', width: 40,
          getInputAdornment: getInputAdornmentServicePointExpression },
        {id:'personServiceCode', label : t('Person service'),  display: true, type: 'string', width: 20, 
          isEditable: cellEditablePackagingProductService, getOptions: getPersonService },

        {id:'productOptionName', label : t('Option'),  display: true, type: 'string', width: 20, 
          isEditable: cellEditablePackagingProductService },

        {id:'quantity', label : t('Quantity'),  display: true, type: 'numeric', width: 10, 
            isEditable: cellEditablePackagingProductQuantity, decimalScale: 4},
                
        {id:'part', label : `${t('Part')}(%)`,  display: true, type: 'numeric', width: 10, 
            isEditable: cellEditablePackagingProduct, decimalScale: 4 },        
      ]);
    }, [t,i18n]);
    

    const refAppendPackagingProducts = useRef<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>(null);
    const refUpdatePackagingProduct = useRef<(index: number,value: Partial<FieldArray<IProduct>> ) => void>(null);
    const refRemovePackagingProduct = useRef<(index: number ) => void>(null);
      
    const handleAddPackagingProducts = (event: any) => {
      setProductFilterUsage('packaging');
      setOpenProductUsageFilter(true);      
    }

    const handleProductOptionChoiceClick = async (productOption : IProductOption) => {
    
        const articleId = currentArticle2Add.id;
        const articleName = currentArticle2Add.name;
        //const articleFilterOption = isFalsy(currentArticle)? currentArticle2Add.filterOption : currentArticle.filterOption;
    
        console.log({articleId,articleName, productOption, productFilterUsage });
        if(productFilterUsage === 'packaging') {
          if(getValues().packagingProducts.some(x => x.productId === productOption.productId && x.productOptionId === productOption.id)) {
            setShowProductOption(false);
            return;
          }
          
          (refAppendPackagingProducts.current??emptyFunc)({id: 0, type: 'article' , packagingId: _id, productId: articleId, 
            quantity: 1, productOptionId: productOption.id, productOptionName: productOption.name, part: 0,
            serviceBeneficiaryPointExpression: '',  productName: articleName, personServiceCode: '' });
        } else if(productFilterUsage === 'stock') {
          console.log({articleId,articleName, productOption });
          setValue('stockProductId', articleId);
          setValue('stockProductName', articleName);
          setValue('stockProductOptionId', productOption.id);
          setValue('stockProductOptionName', productOption.name);
        }        

        setShowProductOption(false);
      }

    const packagingProductRowActionIcon = ( tarificationMatrix: IPackagingProduct) : ActionIconTableRow<IProduct,IPackagingProduct> => {
  
      const res: ActionIconTableRow<IProduct,IPackagingProduct> = {
        toolTip: 'viewDetails',
        icon: RemoveCircleIcon,
        hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
        isActionExecuting: true,
        onRowClickIcon: (event : any,index: number, row: IPackagingProduct) => {
          
           (refRemovePackagingProduct.current??emptyFunc)(index);            
        }
      }
      return res;
  }


  const getFormationBillingTypeCode = (row: IFormationBillingType, cellId: keyof IFormationBillingType, 
    opts: {value: string|number, name: string}[]) : {value: string|number, name: string}[]=> {        
    
      return getAsOptions(refEnumItems.current ?? [],Enum_FORMATION_BILLING_TYPE);
  }

  const cellEditableFormationBillingType = (row: IFormationBillingType, cellId: keyof IFormationBillingType) : boolean => {
    return true;
  }

  const [headFormationBillingTypeCells, setHeadFormationBillingTypeCells]  = useState<HeadCell<IFormationBillingType>[]>([]);
  useEffect(() => {
    setHeadFormationBillingTypeCells([      
      //{id:'userId', label : t('Id'),  display: true, type: 'string', width: 5 },
      {id:'billingTypeCode', label : t('Type'),  display: true, type: 'string', width: 50, isEditable: cellEditableFormationBillingType,
        getOptions: getFormationBillingTypeCode   },
      {id:'dueDate', label : t('Due date'),  display: true, type: 'date', width: 30, isEditable: cellEditableFormationBillingType,},
      {id:'amountPart', label : `${t('Part')}(%)`,  display: true, type: 'numeric', width: 20, 
          isEditable: cellEditableFormationBillingType, decimalScale: 4 },       
    ]);
  }, [t,i18n]);
  

  const refAppendFormationBillingTypes = useRef<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>(null);
  const refUpdateFormationBillingType = useRef<(index: number,value: Partial<FieldArray<IProduct>> ) => void>(null);
  const refRemoveFormationBillingType = useRef<(index: number ) => void>(null);
    
  const handleAddFormationBillingTypes = (event: any) => {
    (refAppendFormationBillingTypes.current??emptyFunc)({id: 0, formationId: _id, billingTypeCode: '', dueDate: new Date(), amountPart: 0, billingTypeName: ''});
  }

  const formationBillingTypeRowActionIcon = ( formationBillingType: IFormationBillingType) : ActionIconTableRow<IProduct,IFormationBillingType> => {

    const res: ActionIconTableRow<IProduct,IFormationBillingType> = {
      toolTip: 'remove',
      icon: RemoveCircleIcon,
      hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event : any,index: number, row: IFormationBillingType) => {
        
         (refRemoveFormationBillingType.current??emptyFunc)(index);            
      }
    }
    return res;
}

const cellEditableRentalResource = (row: IRentalResource, cellId: keyof IRentalResource) => {
  return true;
}

const [headRentalResourceCells, setHeadRentalResourceCells]  = useState<HeadCell<IRentalResource>[]>([]);
useEffect(() => {
  setHeadRentalResourceCells([      
    //{id:'userId', label : t('Id'),  display: true, type: 'string', width: 5 },
    {id:'resourceName', label : t('Resource'),  display: true, type: 'string', width: 50   },
    {id:'isCapacityFixed', label : t('Fixed capacity'),  display: true, type: 'boolean', width: 25, isEditable: cellEditableRentalResource,},
    {id:'defaultCapacity', label : `${t('Default capacity')}`,  display: true, type: 'numeric', width: 25, 
        isEditable: cellEditableRentalResource, decimalScale: 2 },       
  ]);
}, [t,i18n]);


const refAppendRentalResources = useRef<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>(null);
const refUpdateRentalResource = useRef<(index: number,value: Partial<FieldArray<IProduct>> ) => void>(null);
const refRemoveRentalResource = useRef<(index: number ) => void>(null);
  
const handleAddRentalResources = (event: any) => {
  setOpenResourceFilter(true);
}

const rentalResourceRowActionIcon = ( rentalResource: IRentalResource) : ActionIconTableRow<IProduct,IRentalResource> => {

  const res: ActionIconTableRow<IProduct,IRentalResource> = {
    toolTip: 'remove',
    icon: RemoveCircleIcon,
    hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
    isActionExecuting: true,
    onRowClickIcon: (event : any,index: number, row: IRentalResource) => {
      
       (refRemoveRentalResource.current??emptyFunc)(index);            
    }
  }
  return res;
}


const getShipingDensityPriceCode = (row: IShipingDensityPrice, cellId: keyof IShipingDensityPrice, 
  opts: {value: string|number, name: string}[]) : {value: string|number, name: string}[]=> {        
  
    return getAsOptions(refEnumItems.current ?? [],Enum_CURRENCY);
}

const cellEditableShipingDensityPrice = (row: IShipingDensityPrice, cellId: keyof IShipingDensityPrice) => {
  return true;
}

const [headShipingDensityPriceCells, setHeadShipingDensityPriceCells]  = useState<HeadCell<IShipingDensityPrice>[]>([      
  //{id:'userId', label : t('Id'),  display: true, type: 'string', width: 5 },
  {id:'currencyCode', label : t('Currency'),  display: true, type: 'string', width: 20, isEditable: cellEditableShipingDensityPrice,
    getOptions: getShipingDensityPriceCode   },
  {id:'minDensity', label : `${t('Density')} - Min`,  display: true, type: 'numeric', width: 20, 
      isEditable: cellEditableShipingDensityPrice, decimalScale: 4 }, 
  {id:'maxDensity', label : `${t('Density')} - Max`,  display: true, type: 'numeric', width: 20, 
      isEditable: cellEditableShipingDensityPrice, decimalScale: 4 },       
  {id:'referencePrice', label : `${t('Price')} - Ref.`,  display: true, type: 'numeric', width: 20, 
      isEditable: cellEditableShipingDensityPrice, decimalScale: 4 },       
  {id:'minBillingAmount', label : `${t('Amount')} - Min`,  display: true, type: 'numeric', width: 20, 
      isEditable: cellEditableShipingDensityPrice, decimalScale: 4 },   
]);

const refAppendShipingDensityPrices = useRef<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>(null);
const refUpdateShipingDensityPrice = useRef<(index: number,value: Partial<FieldArray<IProduct>> ) => void>(null);
const refRemoveShipingDensityPrice = useRef<(index: number ) => void>(null);
  
const handleAddShipingDensityPrices = (event: any) => {
  (refAppendShipingDensityPrices.current??emptyFunc)({id: 0, productId: _id, currencyCode: applicationSetup.defaultCurrencyCode, 
    minDensity: 0, maxDensity: 0, referencePrice: 0, minBillingAmount: 0 });
}

const shipingDensityPriceRowActionIcon = ( shipingDensityPrice: IShipingDensityPrice) : ActionIconTableRow<IProduct,IShipingDensityPrice> => {

  const res: ActionIconTableRow<IProduct,IShipingDensityPrice> = {
    toolTip: 'remove',
    icon: RemoveCircleIcon,
    hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
    isActionExecuting: true,
    onRowClickIcon: (event : any,index: number, row: IShipingDensityPrice) => {
      
       (refRemoveShipingDensityPrice.current??emptyFunc)(index);            
    }
  }
  return res;
}

const handleStoreDoubleClick = (event: React.MouseEvent<unknown>,row: IArticleStock) => {

  const roleEntityStore = roleEntities.find(re => re.entityName === 'Store');
      if(isFalsy(roleEntityStore)) return;
              
      navigate(`/store/${row.storeId}`);
}

function openFileDialog() {
  (document as any).getElementById("file-upload").click();
}

const setFile = (_event: any) => {
  let f = _event.target.files![0];

  const fileSizeInKB = f.size / 1024;
  // Check if the file size is within your limit (e.g., 200 KB)
  if (fileSizeInKB > 200) {
    alert(t('File size should be less than 200 KB'));
    return;
  }

  var reader = new FileReader();

  reader.onload = function () {

      var binaryString = reader.result as string;
  
      const base64String = binaryString
                                  .replace('data:', '')
                                  .replace(/^.+,/, '');

      setValue("base64Picture", base64String);
      setValue("fileName", f.name);
    };

    reader.onerror = function () {
      console.log("File load failed");
    };    
    reader.readAsDataURL(f);    
};

const debouncedNameChange = useRef(
  debounce( async () => {      
    const { id, name, description} = getValues();

    if(id>0) return;

    if( (isFalsy(name) || name.trim() === '') && (isFalsy(description) || description.trim() === '' )) return;

    const count = await getProductsSearchCount(name, description);

    if(!isFalsy(refProductSnackbarId.current)) closeSnackbar(refProductSnackbarId.current!);
    
    if(count > 0) 
      refProductSnackbarId.current = enqueueSnackbar( `${t('Product')} --> ${t('You have to check if product exists before save, verify it in this list of')} : ${count} ${t('_product')}(s)`, { variant: 'warning',
            anchorOrigin : { horizontal: 'left', vertical: 'bottom' }, persist : true, //autoHideDuration : 5000,
            action: xActionProductSearch() } );             

  }, 2500)
).current;

const refProductSnackbarId = useRef<SnackbarKey>();

const xActionProductSearch = (): SnackbarAction => (snackbarId: SnackbarKey) => (
  <>        
      <Button onClick={() => { 
                           
              setOpenProductSearchFilter(true);
              closeSnackbar(snackbarId); }}>   
          <GrSearch size={24} />
      </Button>
      <Button onClick={() => { closeSnackbar(snackbarId) }}>
          <GrClose size={24} />
      </Button>
  </>
)

useEffect( () => {

  if(isFalsy(watchCurrentCurrencyCode)) return;
       
  const productPrice = getValues().productPrices.find(p => p.currencyCode === watchCurrentCurrencyCode);
  
  if(isFalsy(productPrice)){
    appendProductPrices( {...defaultProductPrice, productId:_id, currencyCode:watchCurrentCurrencyCode });
  } else {
    console.log(productPrice);
    setValue('currentPriceExpression', productPrice.priceExpression, {shouldValidate: true});
  }      
  
}, [ watchCurrentCurrencyCode])


  useEffect( () => {     
          
    debouncedNameChange();      
    }, [watchName, watchDescription, debouncedNameChange]);

    const refEnumItems = useRef<IEnumerationItem[]>([]);    
    useEffect( () => {   
        refEnumItems.current = (enumItems || []);
      
    }, [enumItems])

    const [articleStocks, setArticleStocks] = useState<IArticleStock[]>([]);
    useEffect( () => {
      
      async function _() {   
        if(_data && _data.type === 'article' && _data.id > 0) {         
          const stocks = await getArticlePerStores( 0, _id);
          setArticleStocks(stocks);
        }
        else setArticleStocks([]);
      }
      _();  
    }, [_data] );

      useEffect( () => {        
        setCurrentFormNameAtom(t('Product'));
        setCurrentBasicTextFilterProps(basicFilterProduct);
      }, []);    
    
      /********** This use effect call retreive data wich will call refetch and _data will be updated. 
        and the new useEffect will take place ********************/
        useEffect( () => {
            // setCurrentFormName(t('Billing'));        
            setCurrentFormNameAtom(_id>0?`${t('Product')} - # ${_id} # -`: t('Product') );
            if(_id > 0)
              retrieveData('Product',_id, refetch);  
          }, [_id] );
    
    
        useEffect( () => {
            
        if(_data && _data.id > 0) {
          
            reset( {..._data, currentCurrencyCode: applicationSetup.defaultCurrencyCode,
              currentPriceExpression: '',
              productPrices: (_data.productPrices && _data.productPrices.length>0) ? _data.productPrices :
                 [{...defaultProductPrice, productId:_data.id, currencyCode:applicationSetup.defaultCurrencyCode }],
            } as unknown as IProduct );
          }
        }, [_data]);
    
      const newData = async (event: MouseEvent<HTMLButtonElement>) => {  
       
        _setId(0);      
        setCurrentFormNameAtom( t('Product') );
        reset({...defaultProduct, currentCurrencyCode: applicationSetup.defaultCurrencyCode,
          productPrices: [{...defaultProductPrice, productId:_id, currencyCode:applicationSetup.defaultCurrencyCode }],
          type: applicationSetup.hasService? 'service': 
                  applicationSetup.hasArticle ? 'article' : applicationSetup.hasPackaging? 'packaging':
                    applicationSetup.hasFormation? 'formation' : applicationSetup.hasMaintenance ? 'maintenance' : 
                    applicationSetup.hasContract ? 'contract' : 'fee' });    
      }
      
      const saveData = async (event: MouseEvent<HTMLButtonElement>) => {      
        if(!checkEntitySaveAuthorization('Product', _id)) {
          setIsSaveLoading(false);
             return;
        }          
  
        const data = getValues(); 

        if( isFalsy(data.lineOfBusinessCode) || data.lineOfBusinessCode.trim() === '') {
          enqueueSnackbar( t('Line of business is not specified'), { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }
        
        if(data.name.trim() === '' || data.description.trim() === '') {
            enqueueSnackbar( t('Reference is not specified'), { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
            setIsSaveLoading(false);
            return;
          }

        if(data.type === 'article' && data.productOptions.length <= 0) {
          enqueueSnackbar( t('Article must has one or more options'), { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }

        if(data.type === 'article' && !data.isDirectStock && data.productOptions.length !== 1) {
          enqueueSnackbar( t('Non stockable article must has only one option'), { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }

        const taskCodes = data.serviceTasks.map(x => x.taskCode);
        if( Array.from(new Set(taskCodes)).length !== data.serviceTasks.length) {
          enqueueSnackbar( t('Tasks are not well defined'), { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }
        
        if( data.shipingDensityPrices.some(x => isFalsy(x.currencyCode) )) {
          enqueueSnackbar( t('Invalid data'), { variant: 'warning',
            anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
          setIsSaveLoading(false);
          return;
        }
    
        mutate(data);
      }
    
      const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
        openEntityActionDrawer('Product', _id);
      }

    
      
    const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {          
      queryClient.invalidateQueries(['Product',_id]);        
      await retrieveData('Product',_id, refetch);        
      reset(_data);         
    }

    const [productsBasicImport, setProductsBasicImport] = useState<IProduct[]>([]);
    const [openBasicImportForm, setOpenBasicImportForm] = useState<boolean>(false);
    const basicImportData = async (event: MouseEvent<HTMLButtonElement>) => {          
      setOpenBasicImportForm(true); 
    }

    const handleBasicImportSave = (event: any) => {

      if(productsBasicImport.length === 0) {
        enqueueSnackbar( t('There is no data to import'), { variant: 'warning',
          anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
        return;
      }

      if(!checkEntitySaveAuthorization('Product', _id)) {
        enqueueSnackbar( t('You can not perform this action'), { variant: 'warning',
          anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
        return;
      }

      if(productsBasicImport.some(p => isFalsy(p.name) || isFalsy(p.description) ) ) {
        enqueueSnackbar( t('There are some product without name or description'), { variant: 'warning',
          anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
        return;
      }

      if(productsBasicImport.some(p => isFalsy(p.lineOfBusinessCode) ) ) {
        enqueueSnackbar( t('There are some product without line of business'), { variant: 'warning',
          anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
        return;
      }

      const badLineOfBusiness = productsBasicImport.filter( p => !refEnumItems.current.some( e => 
            e.enumerationCode === Enum_LINE_OF_BUSINESS && e.code === p.lineOfBusinessCode  ) );

      if(badLineOfBusiness.length !== 0 ) {
        enqueueSnackbar( `${t('There are some product with bad Line of business')} : ${badLineOfBusiness[0].name}` , { variant: 'warning',
          anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
        return;
      }

      mutateBasicImport(productsBasicImport);
    }

  return (
    <FormProvider {...methods} >
            <Box sx={{ mx: 0.1 }}>
                <Grid container rowSpacing={0.5} columnSpacing={0.1}>
                    <Grid item xs={12} md={6} lg={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                        <Stack flexDirection='column'  >
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <ToggleButtonGroup value={watchProductType} exclusive key={watchProductType} size="small" onChange={handleProductType} aria-label="text alignment" fullWidth >
                                {applicationSetup.hasService && <ToggleButton value="service" aria-label="centered">
                                  {t('Service')}
                                </ToggleButton> }
                                {applicationSetup.hasFee && <ToggleButton value="fee" aria-label="centered">
                                  {t('Fee')}
                                </ToggleButton> }
                                {applicationSetup.hasArticle && <ToggleButton value="article" aria-label="centered">
                                  {t('Article')}
                                </ToggleButton> }
                                {applicationSetup.hasPackaging && <ToggleButton value="packaging" aria-label="centered">
                                  {t('Packaging')}
                                </ToggleButton> }         
                                {applicationSetup.hasFormation && <ToggleButton value="formation" aria-label="centered">
                                  {t('Formation')}
                                </ToggleButton> }               
                                {applicationSetup.hasMaintenance && <ToggleButton value="maintenance" aria-label="centered">
                                  {t('Maintenance')}
                                </ToggleButton> }
                                {applicationSetup.hasContract && <ToggleButton value="contract" aria-label="centered">
                                  {t('Contract')}
                                </ToggleButton> }
                                {applicationSetup.hasRental && <ToggleButton value="rental" aria-label="centered">
                                  {t('Rental')}
                                </ToggleButton> }
                                {applicationSetup.hasConsumption && <ToggleButton value="consumption" aria-label="centered">
                                  {t('Consumption')}
                                </ToggleButton> }
                                {applicationSetup.hasShiping && <ToggleButton value="shiping" aria-label="centered">
                                  {t('Shiping')}
                                </ToggleButton> }
                                {applicationSetup.hasLaundry && <ToggleButton value="laundry" aria-label="centered">
                                  {t('Laundry')}
                                </ToggleButton> }
                                {applicationSetup.hasExam && <ToggleButton value="exam" aria-label="centered">
                                  {t('Exam')}
                                </ToggleButton> }
                              </ToggleButtonGroup>                                                             
                            </Box> 
                            <Box sx={{ mt: 1, width: '100%' }} >
                                <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                                <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                                <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                                <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />

                                <Button id='btnBasicImport' onClick={basicImportData} sx={ {display:'none'}}  />
                                
                                <TextField sx={{width:'calc(100% - 8px)'}} id="name" label={t('Name')} {...register('name')}
                                  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none',} } } autoFocus  /> 
                                   
                                { openProductSearchFilter && <FormDialog open={openProductSearchFilter} maxWidth='md'
                                  okText='' cancelText='' title={t('Product')} onCancel={()=> {}} 
                                  onClose={()=> {setOpenProductSearchFilter(false);}} onOk={()=> {setOpenProductSearchFilter(false);}}  >
                                      <BasicTextFilterForm<IProduct> {...basicFilterProductSearch } />
                              </FormDialog> }                                                  
                            </Box>   
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(40% - 8px)'}} id="name" label={t('Code')} {...register('code')}
                                  helperText={`${watchCode?watchCode.length:0} / 20`}
                                  inputProps={ { autoComplete: 'new-password', maxLength: 20 ,style: {textTransform: 'none'} } } autoFocus  />  
                              <FormControlLabel sx={{width:'calc(20% - 8px)'}}
                                  label={`${t('Active ?')}`}
                                  control={
                                  <Controller
                                      name='isActive'
                                      control={control}
                                      render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                              />} />
                              <Controller name='lineOfBusinessCode' control={control} 
                                    
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(40% - 8px)'}} id="lineOfBusinessCode"
                                        label={t('Line of business')} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_LINE_OF_BUSINESS ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                            </Box>
                                                       
                                                    
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(100% - 8px)'}} id="description" label={t('Description')} 
                              {...register('description')} /> 
                                              
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField  sx={{width:'calc(100% - 8px)'}} id="tags" label={t('Tags')} {...register('tags')} 
                                          inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } /> 
                                              
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(50% - 8px)'}} id="familyProductName" label={t('Family product')} 
                              {...register('familyProductName')} 
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">                                            
                                    <IconButton color="primary" onClick={handleClickOpenFamilyProduct}>
                                      <ArrowDropDownCircleIcon />
                                    </IconButton>                                                                                               
                                </InputAdornment>
                              ) 
                            }}/> 
                              <TextField sx={{width:'calc(50% - 8px)'}} id="familyName" label={t('Name in family')}
                               {...register('familyName')} 
                               /> 
                                              
                            </Box>
                            
                            <Box sx={{ mt: 0.25, width: '100%' }} > 
                              <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                    sx={{...typographyGroupBoxStyling}}>
                                {`${t(('Price'))} -/- ${t(('Discount'))} -/- ${t(('Loaded'))}`}
                              </Typography>                                                       
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <ToggleButtonGroup value={watchCurrentCurrencyCode} exclusive key={watchCurrentCurrencyCode} 
                                size="small" onChange={handleCurrentCurrency} aria-label="text alignment" fullWidth >
                                  {enumItems && enumItems.filter( e => 
                                                e.enumerationCode === Enum_CURRENCY ).map( (x,idx) =>                                           
                                    <ToggleButton key={`${x.code} - ${x.name}`} value={x.code} aria-label="centered">
                                      {x.name}
                                    </ToggleButton> )
                                  }                                 
                              </ToggleButtonGroup>  
                            </Box>
                            {
                              (getValues().productPrices || []).map( (productPrice, index) => {
                              
                                const { currencyCode, defaultPrice, defaultMaxDiscount, defaultMaxLoaded, 
                                    priceExpression, maxDiscountExpression, isTaxable, isDefaultTaxFormula} = productPrice;
                                
                              return (currencyCode === watchCurrentCurrencyCode) && (
                              <Fragment key={`key-${index}-${currencyCode}-${defaultPrice}-${defaultMaxDiscount}-${defaultMaxLoaded}-${priceExpression}-${maxDiscountExpression}`}>
                                <Box sx={{ mt: 1, width: '100%' }} >
                                  <FormControlLabel sx={{width:'calc(35% - 8px)'}}
                                      label={`${t('Taxable ?')}`}
                                      
                                      control={
                                      <Controller
                                          name={`productPrices.${index}.isTaxable`}
                                          control={control}
                                          render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                  />} />
                                  {isTaxable && <FormControlLabel sx={{width:'calc(65% - 8px)'}}
                                      label={`${t('Default tax formula ?')} : net*tx/100`}
                                      
                                      control={
                                      <Controller
                                          name={`productPrices.${index}.isDefaultTaxFormula`}
                                          control={control}
                                          render={({field: {value, onChange,...props} }) => <Checkbox disabled {...props} checked={value} onChange={onChange} />}                        
                                  />} />  }                                                                                                  
                                </Box>
                                <Box sx={{ mt: 0.25, width: '100%' }} > 
                                  <Controller
                                    render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                      return (
                                        <NumberFormat    
                                          label={`${t('Default')} - ${t('Price')}`} sx={{width:'calc(40% - 8px)'}} disabled={false}
                                          allowEmptyFormatting={false}
                                          control={control}    
                                          thousandSeparator={lg.includes('fr')?' ':','}
                                          decimalScale={4}
                                          onValueChange={ (v) => onChange(v.floatValue) }
                                          defaultValue={value}
                                          value={value}
                                          customInput={TextFieldRight}                            
                                        />
                                      );
                                    }}
                                    name={`productPrices.${index}.defaultPrice`}
                                    control={control}
                                  />      
                                  <Controller
                                    render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                      return (
                                        <NumberFormat    
                                          label={`${t('Default')} - ${t('Max. discount')}`} sx={{width:'calc(30% - 8px)'}} disabled={false}
                                          allowEmptyFormatting={false}
                                          control={control}    
                                          thousandSeparator={lg.includes('fr')?' ':','}
                                          decimalScale={4}
                                          onValueChange={ (v) => onChange(v.floatValue) }
                                          defaultValue={value}
                                          value={value}
                                          customInput={TextFieldRight}                            
                                        />
                                      );
                                    }}
                                    name={`productPrices.${index}.defaultMaxDiscount`}
                                    control={control}
                                  />       
                                  <Controller
                                    render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                      return (
                                        <NumberFormat    
                                          label={`${t('Default')} - ${t('Max. loaded')}`} sx={{width:'calc(30% - 8px)'}} disabled={false}
                                          allowEmptyFormatting={false}
                                          control={control}    
                                          thousandSeparator={lg.includes('fr')?' ':','}
                                          decimalScale={4}
                                          onValueChange={ (v) => onChange(v.floatValue) }
                                          defaultValue={value}
                                          value={value}
                                          customInput={TextFieldRight}                            
                                        />
                                      );
                                    }}
                                    name={`productPrices.${index}.defaultMaxLoaded`}
                                    control={control}
                                  />                                          
                                </Box> 
                                  
                                <Box sx={{ mt: 1, width: '100%' }} key={`price ${watchCurrentPriceExpression} ${index}  ${getValues().productPrices[index].priceExpression}`} >
                                  <Controller 
                                      key={`price exp ${watchCurrentPriceExpression} ${index}  ${getValues().productPrices[index].priceExpression}`}
                                      render={({ field }) => <TextField sx={{width:'calc(100% - 8px)'}} 
                                        key={`price exp ${getValues().currentPriceExpression} ${index} ${getValues().productPrices[index].priceExpression}`}
                                        id="priceExpressionX" 
                                        label={`${t('Expression')} - ${t('Price')} (${index}) :: ${watchCurrentPriceExpression}`} 
                                        //multiline rows={2} 
                                        inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                          InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                              <InputAdornment position="end">                                            
                                                <IconButton color="primary" onClick={(event) =>
                                                    handleClickOpenExpression(event, 'price', `Billing${capitalizeFirstLetter(watchProductType)}`,
                                                                'numeric',getValues().productPrices[index].priceExpression)}>
                                                  <DeveloperModeIcon />
                                                </IconButton>                                                                                               
                                            </InputAdornment>
                                          ) 
                                        }}
                                        helperText={getValues().productPrices[index].priceExpression}
                                    />}
                                    name={`productPrices.${index}.priceExpression`}
                                    //name={`currentPriceExpression`}                                    
                                    control={control}
                                  />                                                                                                                                      
                                </Box>
                              
                                {!isDefaultTaxFormula && <Box sx={{ mt: 1, width: '100%' }} >
                                  <Controller 
                                                                        
                                      name={`productPrices.${index}.taxExpression`}
                                      control={control}
                                      render={({ field }) => 
                                        <TextField sx={{width:'calc(100% - 8px)'}} id="taxExpression" label={`${t('Expression')} - ${t('Tax')}`} 
                                        //multiline rows={2}
                                        inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                        InputProps={{
                                          readOnly: true,
                                          endAdornment: (
                                            <InputAdornment position="end">                                            
                                              <IconButton color="primary" onClick={(event) =>
                                                  handleClickOpenExpression(event, 'tax', `Billing${capitalizeFirstLetter(watchProductType)}`,
                                                              'numeric',getValues().productPrices[index].taxExpression)}>
                                                <DeveloperModeIcon />
                                              </IconButton>                                                                                               
                                          </InputAdornment>
                                        ) 
                                      }}
                                      helperText={getValues().productPrices[index].taxExpression}
                                    />}
                                  />

                                                                                                                                      
                                </Box> }
                                <Box sx={{ mt: 1, width: '100%' }} >
                                  <Controller 
                                    
                                      name={`productPrices.${index}.maxDiscountExpression`}
                                      control={control}
                                      render={({ field }) => <TextField sx={{width:'calc(100% - 8px)'}} id="maxDiscountExpression" label={`${t('Expression')} - ${t('Max. discount')}`} 
                                      //multiline rows={2}
                                        inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                        InputProps={{
                                          readOnly: true,
                                          endAdornment: (
                                            <InputAdornment position="end">                                            
                                              <IconButton color="primary" onClick={(event) =>
                                                  handleClickOpenExpression(event, 'max-discount',`Billing${capitalizeFirstLetter(watchProductType)}`, 
                                                    'numeric',getValues().productPrices[index].maxDiscountExpression)}>
                                                <DeveloperModeIcon />
                                              </IconButton>                                                                                               
                                          </InputAdornment>
                                        ) 
                                      }}
                                      helperText={getValues().productPrices[index].maxDiscountExpression}
                                    />}
                                  />
                                                                                                                                      
                                </Box>
                                <Box sx={{ mt: 1, width: '100%' }} >
                                  <Controller 
                                                                              
                                        name={`productPrices.${index}.maxLoadedExpression`}
                                        control={control}
                                        render={({ field }) => <TextField sx={{width:'calc(100% - 8px)'}} id="maxLoadedExpression" label={`${t('Expression')} - ${t('Max. loaded')}`} 
                                        //multiline rows={2}
                                          inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                          InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                              <InputAdornment position="end">                                            
                                                <IconButton color="primary" onClick={(event) =>
                                                    handleClickOpenExpression(event, 'max-loaded', `Billing${capitalizeFirstLetter(watchProductType)}`,
                                                      'numeric',getValues().productPrices[index].maxLoadedExpression)}>
                                                  <DeveloperModeIcon />
                                                </IconButton>                                                                                               
                                            </InputAdornment>
                                          ) 
                                        }}
                                        helperText={getValues().productPrices[index].maxLoadedExpression}
                                      />}
                                    />                                                                                                                                      
                                </Box>
                                { openProductUsageFilter && watchProductType === 'article' && <Box sx={{ mt: 1, width: '100%' }} >
                                  <Controller 
                                                                             
                                        name={`productPrices.${index}.maxLoadedExpression`}
                                        control={control}
                                        render={({ field }) => <TextField sx={{width:'calc(100% - 8px)'}} id="priceExpression" label={`${t('Expression')} - ${t('Price')} - U`} 
                                        multiline rows={3}
                                          {...register('priceExpressionUnity')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                          InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                              <InputAdornment position="end">                                            
                                                <IconButton color="primary" onClick={(event) =>
                                                    handleClickOpenExpression(event, 'price-unity', 'BillingArticle', 'numeric',getValues().priceExpressionUnity)}>
                                                  <DeveloperModeIcon />
                                                </IconButton>                                                                                               
                                            </InputAdornment>
                                          ) 
                                        }}
                                      />}
                                    />

                                                                                                                                      
                                </Box> }
                              </Fragment> )})
                            }
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(100% - 8px)'}} id="customerPointExpression" label={`${t('Expression')} - ${t('Customer point')}`} 
                                multiline rows={2}
                                  {...register('customerPointExpression')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <InputAdornment position="end">                                            
                                        <IconButton color="primary" onClick={(event) =>
                                            handleClickOpenExpression(event, 'customer-point', `Billing${capitalizeFirstLetter(watchProductType)}`, 
                                              'numeric',getValues().customerPointExpression)}>
                                          <DeveloperModeIcon />
                                        </IconButton>                                                                                               
                                    </InputAdornment>
                                  ) 
                                }}
                              />                                                                                                    
                            </Box> 
                            { watchProductType === 'service' && <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(100% - 8px)'}} id="serviceBeneficiaryPointExpression" label={`${t('Expression')} - ${t('Service beneficiary point')}`} 
                                multiline rows={2}
                                  {...register('serviceBeneficiaryPointExpression')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <InputAdornment position="end">                                            
                                        <IconButton color="primary" onClick={(event) =>
                                            handleClickOpenExpression(event, 'service-point', 'BillingService', 'numeric',getValues().serviceBeneficiaryPointExpression)}>
                                          <DeveloperModeIcon />
                                        </IconButton>                                                                                               
                                    </InputAdornment>
                                  ) 
                                }}
                              />                                                                                                    
                            </Box> }
                            { openEntityExpression && <FormDialog open={openEntityExpression} maxWidth='md'
                                okText={t('OK')} cancelText='' title={`${t('Expression')} ...`} onCancel={()=> {}} 
                                onClose={()=> {setOpenEntityExpression(false);}} onOk={handleClickOkExpression}  >
                                <EntityExpression entityName={currentEntityName} properties={[]} 
                                  expression={currentExpression} setExpression={setCurrentExpression} />
                              </FormDialog>  }
                        </Stack>                        
                      </Grid>
                    <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
                      <Stack flexDirection='column'>      
                        <Box sx={{ mt: 1, width: '100%' }} >                              
                                
                          <TextField sx={{width:'calc(45% - 8px)'}} id="sharingName" label={`${t('Sharing')}`} 
                              {...register('sharingName')}
                              InputProps={{
                                readOnly: true,
                                endAdornment: (
                                  <InputAdornment position="end">   
                                    { (isFalsy(watchSharingId) ||  watchSharingId<=0) ? 
                                      <IconButton color="primary" onClick={handleClickOpenSharing}>
                                        <ArrowDropDownCircleIcon />
                                      </IconButton> :
                                      <IconButton color="primary" onClick={handleClickRemoveSharing}>
                                        <RemoveCircleIcon />
                                      </IconButton>
                                    }                                                                                             
                                </InputAdornment>
                              ) 
                            }}
                          />
                          {watchProductType === 'service' && <Controller                                
                            render={({ field }) => {
                              return (
                                <NumberFormat      
                                  //decimalScale={2}
                                  label={'Duration in minutes'} sx={{width:'calc(20% - 8px)'}}
                                  allowEmptyFormatting={false}
                                  control={control}             
                                                
                                  //fixedDecimalScale={true} 
                                  thousandSeparator={lg.includes('fr')?' ':','}
                                  {...field}
                                  customInput={TextFieldRight}
                                  //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                />
                              );
                            }}
                            name={`serviceDurationInMinute`}
                            //name={`${fieldsName}.${idx}.${cell.id}`}
                            control={control}
                          /> }
                          {watchProductType === 'service' && <Controller                                
                            render={({ field }) => {
                              return (
                                <NumberFormat      
                                  //decimalScale={2}
                                  label={t('Duration in day before renewal')} sx={{width:'calc(35% - 8px)'}}
                                  allowEmptyFormatting={false}
                                  control={control}             
                                                
                                  //fixedDecimalScale={true} 
                                  thousandSeparator={lg.includes('fr')?' ':','}
                                  {...field}
                                  customInput={TextFieldRight}
                                  //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                />
                              );
                            }}
                            name={`durationBeforeServiceRenewalInDay`}
                            //name={`${fieldsName}.${idx}.${cell.id}`}
                            control={control}
                          /> }

                          {watchProductType === 'packaging' && <Controller                                
                            render={({ field }) => {
                              return (
                                <NumberFormat      
                                  decimalScale={2}
                                  label={`${t('Cumulative duration rate')}(%)`} sx={{width:'calc(25% - 8px)'}}
                                  allowEmptyFormatting={false}
                                  control={control}             
                                                
                                  //fixedDecimalScale={true} 
                                  thousandSeparator={lg.includes('fr')?' ':','}
                                  {...field}
                                  customInput={TextFieldRight}
                                  //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                />
                              );
                            }}
                            name={`cumulativeDurationRate`}
                            //name={`${fieldsName}.${idx}.${cell.id}`}
                            control={control}
                          /> }
                          { watchProductType === 'article' && <Controller name='filterOption' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="filterOption"
                                    label={t('Option')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_ARTICLE_OPTION_CLASS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            /> }
                          { watchProductType === 'article' && <Controller name='articleWrapping' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="articleWrapping"
                                    label={t('Wrapping')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_ARTICLE_WRAPPING ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            /> }
                          { watchProductType === 'maintenance' && <Controller name='maintenanceCategoryClass' control={control} 
                                
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(40% - 8px)'}} id="maintenanceCategoryClass"
                                    label={t('Category')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_MAINTENANCE_CATEGORY_CLASS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            /> }
                            { watchProductType === 'contract' && <Controller name='contractScopeClass' control={control} 
                                
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(40% - 8px)'}} id="contractScopeClass"
                                    label={t('Scope')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_CONTRACT_SCOPE_CLASS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            /> }
                            { watchProductType === 'consumption' && <Controller name='consumptionOptionClass' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="consumptionOptionClass"
                                    label={t('Option')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_CONSUMPTION_OPTION_CLASS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            /> }
                            { watchProductType === 'shiping' && <Controller name='shipmentType' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="shipmentType"
                                    label={t('Type')} inputProps={ {readOnly: false}} focused >
                                    <MenuItem key='sea' value='sea'>{t('Maritime')}</MenuItem>
                                    <MenuItem key='air' value='air'>{t('Air_')}</MenuItem>
                                  </TextField>
                                )}
                            /> }
                            { watchProductType === 'exam' && <Controller name='examType' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="consumptionOptionClass"
                                    label={t('Exam type')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_EXAM_TYPE ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            /> }
                            { watchProductType === 'exam' && <FormControlLabel sx={{width:'calc(25% - 8px)'}}
                                    label={`${t('Doctor required ?')}`}
                                    control={
                                    <Controller
                                        name={`doctorRequired`}  
                                        control={control}
                                        render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                />} /> }
                          
                        </Box>
                        { openFamilyProductFilter && <FormDialog open={openFamilyProductFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Family product')} onCancel={()=> {}} 
                              onClose={()=> {setOpenFamilyProductFilter(false);}} onOk={()=> {setOpenFamilyProductFilter(false);}}  >
                                  <BasicTextFilterForm<IFamilyProduct> {...basicFilterFamilyProduct } />
                          </FormDialog> }

                          { openSharingFilter && <FormDialog open={openSharingFilter} maxWidth='md'
                              okText='' cancelText='' title={t('Sharing')} onCancel={()=> {}} 
                              onClose={()=> {setOpenSharingFilter(false);}} onOk={()=> {setOpenSharingFilter(false);}}  >
                                  <BasicTextFilterForm<ISharing> {...basicFilterSharing } />
                          </FormDialog> }
                          { watchProductType === 'exam' && <Box sx={{ mt: 1, width: '100%' }} >
                          <TextField sx={{width:'calc(30% - 8px)'}} id="name" label={t('Required preparation')} 
                                {...register('requiredPreparation')}
                                  helperText={`${watchCode?watchCode.length:0} / 20`}
                                  inputProps={ { autoComplete: 'new-password', maxLength: 20 ,style: {textTransform: 'none'} } } />
                          <Controller                                
                            render={({ field }) => {
                              return (
                                <NumberFormat      
                                  //decimalScale={2}
                                  label={`${t('Exam duration')} - ${t('Minute')}s`} sx={{width:'calc(15% - 8px)'}}
                                  allowEmptyFormatting={false}
                                  control={control}

                                  thousandSeparator={lg.includes('fr')?' ':','}
                                  {...field}
                                  customInput={TextFieldRight}
                                  //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                />
                              );
                            }}
                            name={`examDurationInMinute`}
                            control={control}
                          />
                          <Controller                                
                            render={({ field }) => {
                              return (
                                <NumberFormat      
                                  //decimalScale={2}
                                  label={`${t('Result time')} - ${t('Hour')}s`} sx={{width:'calc(15% - 8px)'}}
                                  allowEmptyFormatting={false}
                                  control={control}

                                  thousandSeparator={lg.includes('fr')?' ':','}
                                  {...field}
                                  customInput={TextFieldRight}
                                  //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                />
                              );
                            }}
                            name={`resultTimeInHour`}
                            control={control}
                          />
                          <Controller name='insuranceCoverageOption' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="consumptionOptionClass"
                                    label={t('Insured')} inputProps={ {readOnly: false}} focused >
                                    <MenuItem value='Y'>{t(('Yes'))}</MenuItem>
                                    <MenuItem value='X'>{`${t(('Yes'))} / ${t(('No'))}`}</MenuItem>
                                    <MenuItem value='N'>{t(('No'))}</MenuItem>
                                  </TextField>
                                )}
                            />

                        </Box> }
                        { watchProductType === 'exam' && watchInsuranceCoverageOption === 'X' && <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IProduct,IExamInsurance,'id'> 
                                mainObject={getValues()} fieldKey='id' 
                                headCells={headExamInsuranceCells} rowsPathName='examInsurances' 
                                title={t('Insurances allowed')} rowActionIcon={examInsuranceRowActionIcon}  
                                //onRowSelected={handleRoleEntitySelected}
                                                    
                                refAppend={refAppendExamInsurances as MutableRefObject<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdateExamInsurance as MutableRefObject<(index: number,value: Partial<FieldArray<IProduct>>) => void>}
                                refRemove={refRemoveExamInsurance as MutableRefObject<(index: number) => void>}

                                toolbarActions={[
                                { toolTip: `${t('Add')}...`, onClickIcon: handleAddExamInsurances ,icon: AddCircleIcon,  },
                                { toolTip: `${t('Add')}... ${t('Insurance')}`, onClickIcon: handleAddInsurances ,icon: ShieldIcon,  },
                                
                                ]}
                                canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                            />
                        </Box> }
                        { ['service', 'packaging', 'rental', 'exam'].includes(watchProductType) && <Box sx={{ mt: 1, width: '100%' }} >
                        
                              <Button variant="contained" onClick={(event: React.MouseEvent<HTMLElement>) => { setAnchorEl(event.currentTarget); }}
                                  sx={{ ml: 1, width: '45%', backgroundColor: getValues().appointmentColor, color: theme.palette.getContrastText(getValues().appointmentColor)}}>
                                  {watchProductType === 'rental'?t('Allocation visualisation color'):t('Appointment visualisation color')}
                              </Button>
                              <Popover
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={() => {setAnchorEl(null);}}
                              >
                                <ChromePicker color={getValues().appointmentColor} 
                                      onChange={(newColor: ColorResult) => {setValue('appointmentColor',newColor.hex); }} />
                              </Popover>
                              { (watchProductType !== 'rental') && <FormControlLabel sx={{width:'calc(50% - 8px)'}}
                                    label={`${t('Can be public appointment ?')}`}
                                    control={
                                    <Controller
                                        name={`canBePublicAppointment`}  
                                        control={control}
                                        render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                />} />}
                            
                            
                        </Box> }                  
                        
                        { watchProductType === 'consumption' && <Box sx={{ mt: 1, width: '100%' }} >
                          <FormControlLabel sx={{width:'calc(50% - 8px)'}}
                                    label={`${t('Is available ?')}`}
                                    control={
                                    <Controller
                                        name='isConsumptionAvailable'
                                        control={control}
                                        render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                />} />                          
                        </Box>  }
                        { watchProductType === 'article' && <Box sx={{ mt: 1, width: '100%' }} >
                          <FormControlLabel sx={{width:'calc(30% - 8px)'}}
                                    label={`${t('Sell without stock ?')}`}
                                    control={
                                    <Controller
                                        name='canSellWithoutStock'
                                        control={control}
                                        render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                />} />
                          <FormControlLabel sx={{width:'calc(35% - 8px)'}}
                              label={`${t('Delivery without stock ?')}`}
                              control={
                              <Controller
                                  name='allowBillingDeliveryWithoutStock'
                                  control={control}
                                  render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                          />} />
                          <Controller                                
                            render={({ field }) => {
                              return (
                                <NumberFormat      
                                  decimalScale={2}
                                  label={t('Reorder Threshold')} sx={{width:'calc(20% - 8px)'}}
                                  allowEmptyFormatting={false}
                                  control={control}

                                  thousandSeparator={lg.includes('fr')?' ':','}
                                  {...field}
                                  customInput={TextFieldRight}
                                  //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                />
                              );
                            }}
                            name={`reorderThreshold`}
                            control={control}
                          />
                        </Box>  }
                        { watchProductType === 'article' && <Box sx={{ mt: 1, width: '100%' }} key={` art stk ${getValues().isDirectStock} ${getValues().stockProductId} ${watchStockProductId}`} >
                          <FormControlLabel sx={{width:'calc(30% - 8px)'}}
                                    label={`${t('Direct stock ?')}`}
                                    control={
                                    <Controller
                                        name='isDirectStock'
                                        control={control}
                                        render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                />} />
                          { watchIsDirectStock && <FormControlLabel sx={{width:'calc(30% - 8px)'}}
                                    label={`${t('Just for stock ?')}`}
                                    control={
                                    <Controller
                                        name='isJustForStock'
                                        control={control}
                                        render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                                />} /> }
                          { !watchIsDirectStock && <TextField sx={{width:'calc(35% - 8px)'}} id="stockProductName" label={t('Stock product')} 
                                {...register('stockProductName')} inputProps={ {readOnly: true }} key={`::${watchStockProductId}`}
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">  
                                    { (isFalsy(watchStockProductId) ||  watchStockProductId<=0) ?
                                        <IconButton color="primary" onClick={handleClickOpenStockProduct}>
                                          <ArrowDropDownCircleIcon />
                                        </IconButton> : 
                                        <IconButton color="primary" onClick={handleClickRemoveStockProduct}>
                                          <RemoveCircleIcon />
                                        </IconButton>
                                      }                                                                                                                                     
                                  </InputAdornment>
                                ) 
                              }} /> }
                              { !watchIsDirectStock && <TextField sx={{width:'calc(25% - 8px)'}} id="stockProductName" label={t('Option')} 
                                {...register('stockProductOptionName')} inputProps={ {readOnly: true }} /> }
                              { !watchIsDirectStock && <Controller                                
                                  render={({ field }) => {
                                    return (
                                      <NumberFormat      
                                        decimalScale={2}
                                        label={t('Quantity')} sx={{width:'calc(10% - 8px)'}}
                                        allowEmptyFormatting={false}
                                        control={control}

                                        thousandSeparator={lg.includes('fr')?' ':','}
                                        {...field}
                                        customInput={TextFieldRight}
                                        //customInput={(props) => <TextField {...props} sx={{width:'calc(20% - 8px)'}} id="roleName" inputProps={{style: { textAlign: 'right' }}} />}
                                      />
                                    );
                                  }}
                                  name={`stockProductQuantity`}
                                  control={control}
                                /> }
                        </Box> }
                        
                        <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IProduct,IProductOption,'id'> 
                              mainObject={getValues()} fieldKey='id' 
                              headCells={headProductOptionCells} rowsPathName='productOptions' 
                              title={t('Options of product')} rowActionIcon={productOptionRowActionIcon}  
                              //onRowSelected={handleRoleEntitySelected}
                                                  
                              refAppend={refAppendProductOptions as MutableRefObject<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>}
                              refUpdate={refUpdateProductOption as MutableRefObject<(index: number,value: Partial<FieldArray<IProduct>>) => void>}
                              refRemove={refRemoveProductOption as MutableRefObject<(index: number) => void>}

                              //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                              //displayMore={undefined}
                              toolbarActions={[
                              { toolTip: `${t('Add')}...`, onClickIcon: handleAddProductOptions ,icon: AddCircleIcon,  },
                              
                              ]}
                              canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                          />                        
                        </Box>
                        <Box sx={{ mt: 4, width: '100%' }} >
                              <Button>
                                {`${t('Add Extension')}`}
                                <Box sx={{ ...justifyCenter, ml: 1 }}>
                                  <MdOutlineAdd size={24} onClick={(event: any) => handleClickAddExtension(event, 'product-A') } />
                                </Box>
                              </Button>
                              { openExtension && <ExtensionFormDialog open={openExtension} maxWidth='sm' height='60vh'
                                    control={controlExtension} setValue={setValueExtension}
                                    okText={t('OK')} cancelText='' title={t('Extension')} onCancel={()=> {}} 
                                    onClose={()=> {setOpenExtension(false);}} onOk={handleOkExtension}  /> } 
                                
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              {                              
                                  (getValues().extensions || []).map( ( {alias, description} ,idx) => 
                                    ( <Chip sx={{ ml: 1, mr:1, mb: 1 }}
                                        key={`${idx} - ${alias}`} label={`${alias} : ${description} `} color="primary" variant="outlined"
                                        onDelete={() => {removeProductExtension(idx)}} 
                                        onClick={() => handleClickEditExtension(idx, 'product-U')}/>
                                        ))
                                }
                            </Box> 
                        { watchProductType === 'service' && <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IProduct,IServiceTask,'id'> 
                                mainObject={getValues()} fieldKey='id' 
                                headCells={headServiceTaskCells} rowsPathName='serviceTasks' 
                                title={t('Tasks of service')} rowActionIcon={serviceTaskRowActionIcon}  
                                //onRowSelected={handleRoleEntitySelected}
                                                    
                                refAppend={refAppendServiceTasks as MutableRefObject<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdateServiceTask as MutableRefObject<(index: number,value: Partial<FieldArray<IProduct>>) => void>}
                                refRemove={refRemoveServiceTask as MutableRefObject<(index: number) => void>}

                                //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                                //displayMore={undefined}
                                toolbarActions={[
                                { toolTip: `${t('Add')}...`, onClickIcon: handleAddServiceTasks ,icon: AddCircleIcon,  },
                                
                                ]}
                                canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                            />
                        </Box> }
                        
                        { watchProductType === 'packaging' && <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IProduct,IPackagingProduct,'id'> 
                              mainObject={getValues()} fieldKey='id' 
                              headCells={headPackagingProductCells} rowsPathName='packagingProducts' 
                              title={t('Products')} rowActionIcon={packagingProductRowActionIcon}  
                              //onRowSelected={handleRoleEntitySelected}
                                                  
                              refAppend={refAppendPackagingProducts as MutableRefObject<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>}
                              refUpdate={refUpdatePackagingProduct as MutableRefObject<(index: number,value: Partial<FieldArray<IProduct>>) => void>}
                              refRemove={refRemovePackagingProduct as MutableRefObject<(index: number) => void>}

                              //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                              //displayMore={undefined}
                              toolbarActions={[
                              { toolTip: `${t('Add')}...`, onClickIcon: handleAddPackagingProducts ,icon: AddCircleIcon,  },
                              
                              ]}
                              canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                          />
                        </Box> }
                        <Box sx={{ mt: 5, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', }} >
                          <input type="file" id="file-upload" style={{ display: "none" }} onChange={setFile}
                            accept="image/*" />
                          <div key={'step.id'}>
                            <Box
                              component="img"
                              sx={carouselImage}
                              //src={`data:image/png;base64,${watchBase64Picture}`}
                              src={isFalsy(watchBase64Picture)?`/images/product.jpg`:`data:image/png;base64,${watchBase64Picture}`}
                              onClick={openFileDialog}
                              //alt={step.id}
                            ></Box>
                          </div>                              
                        </Box>
                        { watchProductType === 'article' && <Box sx={{ mt: 1, width: '100%' }} >
                          <EnhancedTable<IArticleStock> rows={articleStocks} 
                            headCells={[            
                              //{id:'id', label : t('Id'),  display: true, type: 'numeric', },
                              {id:'storeName', label : t('_Store'),  display: true, type: 'string', width: 33},
                              {id:'productOptionName', label : t('Option'),  display: true, type: 'string', width: 12},
                              
                              {id:'quantityDelivery', label : `${t('_Deli')}(+)`,  display: true, type: 'numeric', decimalScale:2, width: 10},   
                              {id:'quantityBilling', label : `${t('_Bil')}(-)`,  display: true, type: 'numeric', decimalScale:2, width: 9},     
                              {id:'quantityTransfertVariation', label : `${t('_Trsf')}`,  display: true, type: 'numeric', decimalScale:2, width: 9},  
                              {id:'quantityMovementVariation', label : `${t('_Mvt')}`,  display: true, type: 'numeric', decimalScale:2, width: 8}, 
                                           
                              {id:'quantityInventoryVariation', label : `${t('_Inv')}`,  display: true, type: 'numeric', decimalScale:2, width: 9},                  
                              {id:'quantity', label : t('Qty'),  display: true, type: 'numeric', decimalScale:2, width: 10}, 
                            ]} 
                            title={`${t(`Quantities per store`)} - ${t('Total')} : ${sum(articleStocks.map(x => x.quantity))}`} objKey='storeName' 
                            stateSelected={undefined} 
                            onRowSelected={undefined} rowCheckedMode='single'
                            onRowCheckedSelectChange={undefined} order='asc' orderBy='storeName'
                            onRowDoubleClick={handleStoreDoubleClick} 
                            rowActionIcon={undefined}
                            toolbarActions={[
                              // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                            ]}
                          />
                        </Box>
                        }
                        { watchProductType === 'formation' && <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IProduct,IFormationBillingType,'id'> 
                                mainObject={getValues()} fieldKey='id' 
                                headCells={headFormationBillingTypeCells} rowsPathName='formationBillingTypes' 
                                title={t('Payment schedule')} rowActionIcon={formationBillingTypeRowActionIcon}  
                                //onRowSelected={handleRoleEntitySelected}
                                                    
                                refAppend={refAppendFormationBillingTypes as MutableRefObject<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdateFormationBillingType as MutableRefObject<(index: number,value: Partial<FieldArray<IProduct>>) => void>}
                                refRemove={refRemoveFormationBillingType as MutableRefObject<(index: number) => void>}

                                //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                                //displayMore={undefined}
                                toolbarActions={[
                                { toolTip: `${t('Add')}...`, onClickIcon: handleAddFormationBillingTypes ,icon: AddCircleIcon,  },
                                
                                ]}
                                canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                            />
                        </Box> }
                        { watchProductType === 'rental' && <Box sx={{ mt: 1, width: '100%' }} >
                          <Controller name='rentalPeriodicity' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(40% - 8px)'}} id="rentalPeriodicity"
                                    label={t('Periodicity')} inputProps={ {readOnly: false}} focused >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_RENTAL_PERIODICITY ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            />
                        </Box>}
                        { watchProductType === 'rental' && <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IProduct,IRentalResource,'id'> 
                                mainObject={getValues()} fieldKey='id' 
                                headCells={headRentalResourceCells} rowsPathName='rentalResources' 
                                title={t('Allocated resources')} rowActionIcon={rentalResourceRowActionIcon}  
                                //onRowSelected={handleRoleEntitySelected}
                                                    
                                refAppend={refAppendRentalResources as MutableRefObject<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdateRentalResource as MutableRefObject<(index: number,value: Partial<FieldArray<IProduct>>) => void>}
                                refRemove={refRemoveRentalResource as MutableRefObject<(index: number) => void>}

                                //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                                //displayMore={undefined}
                                toolbarActions={[
                                { toolTip: `${t('Add')}...`, onClickIcon: handleAddRentalResources ,icon: AddCircleIcon,  },
                                
                                ]}
                                canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                            />
                        </Box> }
                        { watchProductType === 'shiping' && <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IProduct,IShipingDensityPrice,'id'> 
                                mainObject={getValues()} fieldKey='id' 
                                headCells={headShipingDensityPriceCells} rowsPathName='shipingDensityPrices' 
                                title={t('Tasks of service')} rowActionIcon={shipingDensityPriceRowActionIcon}  
                                //onRowSelected={handleRoleEntitySelected}
                                                    
                                refAppend={refAppendShipingDensityPrices as MutableRefObject<(value: Partial<FieldArray<IProduct>> | Partial<FieldArray<IProduct>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdateShipingDensityPrice as MutableRefObject<(index: number,value: Partial<FieldArray<IProduct>>) => void>}
                                refRemove={refRemoveShipingDensityPrice as MutableRefObject<(index: number) => void>}

                                //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                                //displayMore={undefined}
                                toolbarActions={[
                                { toolTip: `${t('Add')}...`, onClickIcon: handleAddShipingDensityPrices ,icon: AddCircleIcon,  },
                                
                                ]}
                                canCheckRow={false} //canFilterColumn={false} //canDisplayColumnHeader={false}
                            />
                        </Box> }
                        
                        { openResourceFilter && <FormDialog open={openResourceFilter} maxWidth='md' 
                                  okText='' cancelText='' title={t('Resource')} onCancel={()=> {}} 
                                  onClose={()=> {setOpenResourceFilter(false);}} onOk={()=> {setOpenResourceFilter(false);}}  >
                                      <BasicTextFilterForm<IResource> {...basicFilterResource } />
                              </FormDialog> }
                        { openProductUsageFilter && <FormDialog open={openProductUsageFilter} maxWidth='md'
                                okText={t('OK')} cancelText={t('Cancel')} title={t('Product filter')} onCancel={()=> {setOpenProductUsageFilter(false);}} 
                                onClose={()=> {setOpenProductUsageFilter(false);}} onOk={()=> {setOpenProductUsageFilter(false);}}  >
                                    <BasicTextFilterForm<IProduct> {...basicFilterProductUsage } />
                              </FormDialog> }
                        { showProductOption && <FormDialog open={showProductOption} maxWidth='xs' height='50vh'
                                  okText={t('Close')} cancelText='' title='' onCancel={()=> {}} 
                                  onClose={()=> {setShowProductOption(false);}} onOk={()=> {setShowProductOption(false);}}  >
                                      <BasicButtonList<IProductOption> 
                                          items={currentProductOptions} 
                                          icon={BoltIcon} 
                                          onItemClick={handleProductOptionChoiceClick} 
                                          valueKey={'id'} displayKey={'name'} title={t('Product options')} />
                          </FormDialog> }
                        { openBasicImportForm && <FormDialog open={openBasicImportForm} maxWidth='sm' height='45vh'
                                okText={t('Save')} cancelText={t('Cancel')} title={`${t('Import')} ....`} onCancel={()=> {setOpenBasicImportForm(false);}} 
                                onClose={()=> {setOpenBasicImportForm(false);}} onOk={handleBasicImportSave}  >
                                    <BasicImportForm<IProduct> 
                                      stateItems={[productsBasicImport, setProductsBasicImport]} 
                                      columns={['name','description', 'code', 'lineOfBusinessCode', 
                                        /*'priceExpression', 'priceExpressionUnity'*/]}
                                      defaultItem={defaultProduct} />
                              </FormDialog> }
                              { displayEnumerationItemDialog && <DialogEnumerationItemForm 
                                      {...{open: displayEnumerationItemDialog, 
                                            title: '', 
                                            enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
                                            afterSave: async () => {await refetchEnumerationItems()}}} 
                                      />
                                  }   
                      </Stack>
                    </Grid>     
                                                  
                </Grid>
            </Box>
        </FormProvider> 
  )
}

